import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { TouchTarget } from './button';
import { Link } from './link';

const colors = {
  gray: 'bg-ds-gray-200/50 text-ds-gray-900 group-data-hover:bg-ds-gray-200/70',
  blue: 'bg-ds-blue-200/50 text-ds-blue-900 group-data-hover:bg-ds-blue-200/70',
  red: 'bg-ds-red-200/50 text-ds-red-900 group-data-hover:bg-ds-red-200/70',
  amber: 'bg-ds-amber-200/50 text-ds-amber-900 group-data-hover:bg-ds-amber-200/70',
  teal: 'bg-ds-teal-200/50 text-ds-teal-900 group-data-hover:bg-ds-teal-200/70',
  purple: 'bg-ds-purple-200/50 text-ds-purple-900 group-data-hover:bg-ds-purple-200/70',
  pink: 'bg-ds-pink-200/50 text-ds-pink-900 group-data-hover:bg-ds-pink-200/70',
  zinc: 'bg-muted text-muted-foreground group-data-hover:bg-muted/80',
};

type BadgeProps = { color?: keyof typeof colors; compact?: boolean };

export function Badge({
  color = 'zinc',
  compact = false,
  className,
  ...props
}: BadgeProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'inline-flex items-center rounded-xs border border-current/15 forced-colors:outline',
        compact
          ? 'gap-x-1 px-1 py-0 h-[18px] text-[10px]/4 font-normal [&>svg]:size-2.5 [&>svg]:shrink-0'
          : 'gap-x-1.5 px-1.5 py-0.5 text-sm/5 sm:text-xs/5',
        colors[color]
      )}
    />
  );
}

export const BadgeButton = forwardRef(function BadgeButton(
  {
    color = 'zinc',
    className,
    children,
    ...props
  }: BadgeProps & { className?: string; children: React.ReactNode } & (
      | ({ href?: never } & Omit<Headless.ButtonProps, 'as' | 'className'>)
      | ({ href: string } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)
    ),
  ref: React.ForwardedRef<HTMLElement>
) {
  let classes = clsx(
    className,
    'group relative inline-flex rounded-sm focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary'
  );

  return typeof props.href === 'string' ? (
    <Link {...props} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...props} className={classes} ref={ref}>
      <TouchTarget>
        <Badge color={color}>{children}</Badge>
      </TouchTarget>
    </Headless.Button>
  );
});
